import { INavData } from '@coreui/angular';

import { ORG_ADMIN } from '../../models/org-roles';
import { OrgPage, orgPageOf, orgUrl } from '../../org-url';

/** `org` is not a platform rank: it marks the surfaces of the ORG tier
 *  (org staff, and a super admin acting-as an org). Platform staff
 *  never see org items without act-as; org staff see ONLY org items
 *  plus the shared, backend-scoped posture screens. */
export type NavTier =
  'all' | 'support' | 'marketing' | 'admin' | 'super' | 'org';

/** Minimum role tier per sidebar surface, keyed by nav url. The sidebar
 *  filter and the staff permission preview both read this, so a new
 *  surface needs exactly one row here besides its navItems entry.
 *  Cosmetic, as ever: the backend gates are the real ones. */
export const NAV_TIERS: Record<string, NavTier> = {
  users: 'support',
  guests: 'support',
  posture: 'support',
  // Not menu items, but the ticket thread and the new-ticket form: the
  // route guard reads this map by first segment too.
  tickets: 'support',
  messages: 'support',
  'add-ticket': 'support',
  broadcast: 'marketing',
  'email-templates': 'marketing',
  staff: 'admin',
  audit: 'admin',
  'canned-replies': 'admin',
  settings: 'super',
  'deletion-requests': 'super',
  'grant-requests': 'super',
  'bulk-credits': 'super',
  organizations: 'super',
  'org-activity': 'super',
  'org/dashboard': 'org',
  'org/people': 'org',
  'org/reports': 'org',
  'org/pool': 'org',
  'org/activity': 'org',
  'org/support': 'org',
  'org/settings': 'org',
};

/** Minimum ORG RANK per org surface, for the surfaces that need one.
 *  Ranks outrank downwards (OWNER=1, ADMIN=2, SUPPORT=3), so this is a
 *  ceiling on `orgRole`. Mirrors `require_org_admin` on the backend:
 *  Activity was in every org sidebar but 403s below ADMIN, so Support
 *  staff only ever saw "Could not load the activity".
 *
 *  Surfaces absent from this map are open to every rank, and that is the
 *  only thing this map may express. It exists to stop the sidebar
 *  offering a page the API REFUSES, never to invent a restriction of its
 *  own: Settings is readable at every rank (`require_org_access`), so it
 *  is deliberately absent even though only an OWNER may write it. */
export const ORG_MIN_RANK: Partial<Record<OrgPage, number>> = {
  activity: ORG_ADMIN,
};

/** PLATFORM SCOPE: what a muufree account reaches, grouped by what the
 *  destination IS rather than by who may see it (approved mock 1B-1).
 *  Every group drops whole when a role has nothing in it, so a shorter
 *  list is never an unexplained one.
 *
 *  An organization's own console is NOT here: it lives in `orgNavItems`
 *  and the scope switcher swaps the whole menu for it. Appending the two
 *  is what made a super admin acting-as read 28 items with `X-Org-Context`
 *  silently applied to both halves. */
export const navItems: INavData[] = [
  {
    name: 'Dashboard',
    url: '/dashboard',
    iconComponent: { name: 'cil-speedometer' },
  },
  {
    name: 'Across all orgs',
    title: true
  },
  {
    name: 'Users',
    iconComponent: { name: 'cilUser' },
    url: 'users'
  },
  {
    name: 'Guests',
    iconComponent: { name: 'cilUserUnfollow' },
    url: 'guests'
  },
  {
    // A queue of USER accounts asking to be deleted (the user list with
    // RequestedForDelete), so it belongs with the people screens rather
    // than under ORGANIZATIONS, where it sat until 2026-09-20.
    name: 'Deletion requests',
    iconComponent: { name: 'cilTrash' },
    url: 'deletion-requests'
  },
  {
    name: 'Tickets',
    iconComponent: { name: 'cilHeadphones' },
    url: 'tickets'
  },
  {
    name: 'Posture',
    iconComponent: { name: 'cilChart' },
    url: 'posture'
  },
  {
    name: 'Content',
    title: true
  },
  {
    name: 'Articles',
    iconComponent: { name: 'cilDescription' },
    url: 'articles'
  },
  {
    name: 'Categories',
    iconComponent: { name: 'cilTag' },
    url: 'article-categories'
  },
  {
    name: 'Learn content',
    iconComponent: { name: 'cilPuzzle' },
    url: 'learn'
  },
  {
    name: 'Messaging',
    title: true
  },
  {
    name: 'Notifications',
    iconComponent: { name: 'cilEnvelopeClosed' },
    url: 'notification-templates'
  },
  {
    name: 'Emails',
    iconComponent: { name: 'cilEnvelopeLetter' },
    url: 'email-templates'
  },
  {
    name: 'Broadcast',
    iconComponent: { name: 'cilBell' },
    url: 'broadcast'
  },
  {
    name: 'Launch waitlist',
    iconComponent: { name: 'cilBell' },
    url: 'notify-relese'
  },
  {
    name: 'Organizations',
    title: true
  },
  {
    name: 'Organizations',
    iconComponent: { name: 'cilBuilding' },
    url: 'organizations'
  },
  {
    name: 'Grant requests',
    iconComponent: { name: 'cilCheckCircle' },
    url: 'grant-requests'
  },
  {
    name: 'Org activity',
    iconComponent: { name: 'cilFingerprint' },
    url: 'org-activity'
  },
  {
    name: 'Platform admin',
    title: true
  },
  {
    name: 'Staff',
    iconComponent: { name: 'cilUserFollow' },
    url: 'staff'
  },
  {
    name: 'Audit log',
    iconComponent: { name: 'cilFingerprint' },
    url: 'audit'
  },
  {
    name: 'Saved replies',
    iconComponent: { name: 'cilCommentSquare' },
    url: 'canned-replies'
  },
  {
    // Not an ORGANIZATIONS item however it looks: every scope it offers
    // is platform-wide (all active users, active in N days, staff, by
    // email) and `_bulk_target_ids` never joins an org. It sits beside
    // the Settings page it was split out of on 2026-09-16.
    name: 'Bulk credits',
    iconComponent: { name: 'cilLayers' },
    url: 'bulk-credits'
  },
  {
    // "Settings" alone was ambiguous the moment an organization got its
    // own: these are two different destinations.
    name: 'Platform settings',
    iconComponent: { name: 'cilSettings' },
    url: 'settings'
  },
];

/** ORG SCOPE: the whole org console, and all of it. Every url here is an
 *  `ORG_PAGES` value, rewritten onto the org's own `/o/<slug>` base by the
 *  layout, so no click leaves the organization's address.
 *
 *  ORGANIZATION holds the org itself rather than the daily work. It keeps
 *  its label at both sizes: an owner or admin sees Activity and Settings,
 *  a support rank sees Settings alone. */
export const orgNavItems: INavData[] = [
  {
    name: 'Dashboard',
    iconComponent: { name: 'cil-speedometer' },
    url: 'org/dashboard'
  },
  {
    name: 'Manage',
    title: true
  },
  {
    name: 'People',
    iconComponent: { name: 'cilUser' },
    url: 'org/people'
  },
  {
    name: 'Posture reports',
    iconComponent: { name: 'cilChart' },
    url: 'org/reports'
  },
  {
    name: 'Scan pool',
    iconComponent: { name: 'cilCreditCard' },
    url: 'org/pool'
  },
  {
    name: 'Support',
    iconComponent: { name: 'cilCommentSquare' },
    url: 'org/support'
  },
  {
    name: 'Organization',
    title: true
  },
  {
    name: 'Activity',
    iconComponent: { name: 'cilFingerprint' },
    url: 'org/activity'
  },
  {
    name: 'Settings',
    iconComponent: { name: 'cilSettings' },
    url: 'org/settings'
  },
];

/** What a badge fetch found, keyed by the item's own url. */
export interface NavBadge {
  color: string;
  text: string;
}

/** Everything the menu needs to know about the caller. Kept as plain data
 *  so the rule below can be tested without a component, a TestBed or a
 *  single HTTP stub. */
export interface NavScope {
  /** Inside an organization: org staff always, a super admin while
   *  acting-as. */
  inOrgScope: boolean;
  /** The org's own base, which every org item is rewritten onto. */
  orgSlug: string | null;
  /** The caller's ORG rank, or undefined for a visiting super admin, who
   *  holds none and is admitted by `scope_org_id` instead. */
  orgRank?: number;
  isSupport: boolean;
  /** The outbound channels: admins plus MARKETING. */
  isMarketing: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

/** A group heading belongs to the items under it, so a heading with
 *  nothing left after filtering has to go too. Without this a role that
 *  can reach none of a section's pages reads a bare label and wonders
 *  what is missing. */
function dropEmptyGroups(items: INavData[]): INavData[] {
  return items.filter((item, i) => {
    if (!item.title) return true;
    for (let j = i + 1; j < items.length; j++) {
      if (items[j].title) break;
      return true;
    }
    return false;
  });
}

/** The menu for the scope the caller is in.
 *
 *  Two separate lists, never concatenated: appending the org console to
 *  the platform list is what made a super admin acting-as read 28
 *  undifferentiated items with `X-Org-Context` silently applied to both
 *  halves. Stepping into an org REPLACES the menu.
 *
 *  Cosmetic, as ever: the gate that matters is the backend's. */
export function menuFor(
  scope: NavScope, badges: Record<string, NavBadge> = {}
): INavData[] {
  const withBadge = (item: INavData) => {
    const badge = badges[String(item.url ?? '')];
    return badge ? { ...item, badge } : item;
  };

  if (scope.inOrgScope) {
    const slug = scope.orgSlug;
    if (!slug) return [];
    const allowed = orgNavItems.filter(item => {
      if (item.title) return true;
      // The sidebar must not offer a page the API refuses this rank.
      const page = orgPageOf(String(item.url ?? ''));
      const min = page ? ORG_MIN_RANK[page] : undefined;
      const rank = scope.orgRank;
      return !(min !== undefined && rank !== undefined && rank > min);
    });
    // Every org surface lives on the org's own base, so that is what the
    // sidebar links to: no click leaves /o/<slug>. The badge is read off
    // the ORIGINAL url, because that is the key the fetch wrote and the
    // rewrite would lose it.
    return dropEmptyGroups(allowed).map(item => {
      const badged = withBadge(item);
      const page = item.url ? orgPageOf(String(item.url)) : null;
      return page ? { ...badged, url: orgUrl(slug, page) } : badged;
    });
  }

  const allowed: Record<NavTier, boolean> = {
    all: true,
    support: scope.isSupport,
    marketing: scope.isMarketing,
    admin: scope.isAdmin,
    super: scope.isSuperAdmin,
    // Platform staff reach the org tier only by stepping into an org,
    // which is the branch above.
    org: false,
  };
  const platform = navItems.filter(item => {
    if (item.title) return true;
    return allowed[NAV_TIERS[String(item.url ?? '')] ?? 'all'];
  });
  return dropEmptyGroups(platform).map(withBadge);
}

/** The class a nav item wears while the heading above it is folded away.
 *  The item stays in the DOM and CSS hides it, so the narrow (icon-only)
 *  sidebar, which renders no headings at all, can bring every icon back
 *  with one rule instead of stranding a section behind a heading that is
 *  not there to click. */
export const NAV_FOLDED = 'app-sec-hidden';

/** A heading's key in the folded set: its own name, lowercased. */
export function sectionKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

/** Makes every group heading a real `<button>` (CoreUI renders the title
 *  text inside `wrapper`, so Enter and Space work without a keydown
 *  handler of our own) and marks the items belonging to a folded one.
 *
 *  Open is the default and only the folded keys are remembered, so a
 *  heading added later is open for everybody. */
export function foldSections(
  items: INavData[], folded: ReadonlySet<string>
): INavData[] {
  let hidden = false;
  return items.map(item => {
    if (!item.title) {
      return hidden
        ? { ...item, class: `${item.class ?? ''} ${NAV_FOLDED}`.trim() }
        : item;
    }
    const key = sectionKey(String(item.name ?? ''));
    hidden = folded.has(key);
    return {
      ...item,
      class: `${item.class ?? ''} app-sec`.trim(),
      wrapper: {
        element: 'button',
        attributes: {
          type: 'button',
          class: 'app-sec__btn',
          'data-sec': key,
          'aria-expanded': String(!hidden)
        }
      }
    };
  });
}
